import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ReportView } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import type { CrewStatisticsResponse, CrewStatsAvailableRow } from '../types';
import { AvailableCountReport } from './AvailableCountReport';
import { availableCountBackPath, availableCountListApiUrl } from './availableCountPrintMeta';

const AvailableCountReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [searchParams] = useSearchParams();

  const listUrl = useMemo(() => availableCountListApiUrl(searchParams), [searchParams]);
  const { data, error, isLoading } = useSWR<CrewStatisticsResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const vesselId = searchParams.get('vesselId')?.trim() ?? '';
  const available = useMemo(
    () => (data?.lists?.available ?? []) as CrewStatsAvailableRow[],
    [data?.lists?.available],
  );

  const vesselLabel = useMemo(() => {
    if (data?.vessel?.vesselName) return data.vessel.vesselName;
    if (!vesselId) return 'All vessels';
    return `Vessel #${vesselId}`;
  }, [data?.vessel?.vesselName, vesselId]);

  const backTo = availableCountBackPath();

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading available counts…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className={cn(
          'flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4',
          colors.background,
        )}
      >
        <p className={cn('text-sm', colors.textMuted)}>Could not load available statistics.</p>
        <Link to={backTo} className={cn('text-sm font-medium hover:underline', colors.primaryFg)}>
          ← Back to Available
        </Link>
      </div>
    );
  }

  return (
    <ReportView
      backTo={backTo}
      backLabel="Back to Available"
      title="Available counts"
      subtitle={`${vesselLabel} · ${available.length} crew`}
      printPageSize="A4 landscape"
      printMargin="0"
    >
      <AvailableCountReport available={available} vesselLabel={vesselLabel} />
    </ReportView>
  );
};

export default AvailableCountReportPage;
